// Constants
import {
  BASE_Y_POSITION,
  CARD_GAP,
  ODD_X_OFFSET,
  PHASE_1_MAX_CARDS,
  VERTICAL_OFFSET,
} from './constants';

// Types
import { CardPosition, TeamMember } from '../../types';

/**
 * Calculate evenly distributed horizontal positions for cards
 * Supports up to 5 cards, distributes them centered horizontally
 * with alternating Y positions for visual variety
 *
 * @param cardCount - Number of cards (1-5)
 * @param containerWidth - Width of the container
 * @param cardWidth - Width of each card
 * @returns Array of {x, y} positions
 */
export const calculateCardPositions = (
  cardCount: number,
  containerWidth: number,
  cardWidth: number,
): CardPosition[] => {
  // Clamp card count to 1-5
  const count = Math.max(1, Math.min(5, cardCount));

  // Increase gap when fewer than 4 cards are displayed
  const gap = count < 4 ? CARD_GAP + 50 : CARD_GAP;

  // Total width occupied by all cards including gaps
  const totalWidth = count * cardWidth + (count - 1) * gap;

  // Starting X position to center all cards
  const startX = (containerWidth - totalWidth) / 2;

  return Array.from({ length: count }, (_, index) => {
    // Calculate X position for each card
    const baseX = startX + index * (cardWidth + gap);
    const x = index % 2 === 0 ? baseX - ODD_X_OFFSET : baseX;

    // Alternate Y position: even indices at baseY, odd indices lower
    const y =
      index % 2 === 0 ? BASE_Y_POSITION : BASE_Y_POSITION + VERTICAL_OFFSET;

    return { x, y };
  });
};

/**
 * Split members into phases: only when there are exactly 5 members,
 */
export const splitIntoPhases = (members: TeamMember[]): TeamMember[][] => {
  if (members.length !== 5) {
    return [members];
  }

  return [
    members.slice(0, PHASE_1_MAX_CARDS),
    members.slice(PHASE_1_MAX_CARDS),
  ];
};
